import React, { useCallback, useMemo, useState } from "react";

import { faArrowTurnDownLeft } from "@fortawesome/pro-solid-svg-icons/faArrowTurnDownLeft";
import { faArrowUp } from "@fortawesome/pro-solid-svg-icons/faArrowUp";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faSquare } from "@fortawesome/pro-solid-svg-icons/faSquare";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import EmailTone from "@mapmycustomers/shared/enum/email/EmailTone";
import { Button, Dropdown, TextField } from "@mapmycustomers/ui";

import messages from "./messages";
import styles from "./PromptInput.module.scss";

const TONE_OPTIONS = [
  EmailTone.PROFESSIONAL,
  EmailTone.FRIENDLY,
  EmailTone.CASUAL,
  EmailTone.FORMAL,
  EmailTone.PERSUASIVE,
  EmailTone.URGENT,
  EmailTone.NEUTRAL,
];

const emailToneMessages = defineMessages<EmailTone>({
  [EmailTone.CASUAL]: {
    id: "email.creation.aiEmailWriter.tone.casual",
    defaultMessage: "Casual",
    description: "Tone option for casual emails",
  },
  [EmailTone.FORMAL]: {
    id: "email.creation.aiEmailWriter.tone.formal",
    defaultMessage: "Formal",
    description: "Tone option for formal emails",
  },
  [EmailTone.FRIENDLY]: {
    id: "email.creation.aiEmailWriter.tone.friendly",
    defaultMessage: "Friendly",
    description: "Tone option for friendly emails",
  },
  [EmailTone.NEUTRAL]: {
    id: "email.creation.aiEmailWriter.tone.neutral",
    defaultMessage: "Neutral",
    description: "Tone option for neutral emails",
  },
  [EmailTone.PERSUASIVE]: {
    id: "email.creation.aiEmailWriter.tone.persuasive",
    defaultMessage: "Persuasive",
    description: "Tone option for persuasive emails",
  },
  [EmailTone.PROFESSIONAL]: {
    id: "email.creation.aiEmailWriter.tone.professional",
    defaultMessage: "Professional",
    description: "Tone option for professional emails",
  },
  [EmailTone.URGENT]: {
    id: "email.creation.aiEmailWriter.tone.urgent",
    defaultMessage: "Urgent",
    description: "Tone option for urgent emails",
  },
});

interface PromptInputProps {
  focused: boolean;
  generating: boolean;
  hasGeneratedContent: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  onFocusChange: (focused: boolean) => void;
  onStop?: () => void;
  onSubmit: () => void;
  onToneSelect: (tone: EmailTone) => void;
  placeholder?: string;
  selectedTone?: EmailTone;
  value: string;
}

const PromptInput = ({
  focused,
  generating,
  hasGeneratedContent,
  onChange,
  onClose,
  onConfirm,
  onFocusChange,
  onStop,
  onSubmit,
  onToneSelect,
  placeholder,
  selectedTone,
  value,
}: PromptInputProps) => {
  const intl = useIntl();

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey && value.trim() && !generating) {
        e.preventDefault();
        onSubmit();
      }
    },
    [generating, onSubmit, value]
  );

  const handleChange = useCallback(
    (value: string) => {
      onChange(value);
      setDropdownOpen(false);
    },
    [onChange]
  );

  const handleFocus = useCallback(() => {
    onFocusChange(true);
    if (!generating && !hasGeneratedContent) {
      setDropdownOpen(true);
    }
  }, [generating, hasGeneratedContent, onFocusChange]);

  const handleBlur = useCallback(() => {
    onFocusChange(false);
    setDropdownOpen(false);
  }, [onFocusChange]);

  const handleToneSelect = useCallback(
    (tone: EmailTone) => {
      onToneSelect(tone);
      setDropdownOpen(false);
    },
    [onToneSelect]
  );

  const hasValue = !!value.trim().length;

  const toneMenuItems = useMemo(
    () =>
      TONE_OPTIONS.map((tone) => ({
        key: tone,
        label: intl.formatMessage(emailToneMessages[tone]),
        onClick: () => handleToneSelect(tone),
      })),
    [handleToneSelect, intl]
  );

  return (
    <Dropdown
      align={{ offset: [-10, 15] }}
      dropdownRender={(menu) => <div onMouseDown={(e) => e.preventDefault()}>{menu}</div>}
      menu={{
        className: styles.toneMenu,
        items: toneMenuItems,
        selectedKeys: selectedTone ? [selectedTone] : [],
      }}
      open={dropdownOpen && !generating && !hasGeneratedContent}
      overlayClassName={styles.overlay}
      placement="bottomLeft"
      trigger={[]}
    >
      <div className={styles.container}>
        <TextField
          autoFocus={!generating}
          bordered={false}
          className={styles.input}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? intl.formatMessage(messages.promptPlaceholder)}
          readOnly={generating}
          size="small"
          suffix={
            <div className={styles.actions}>
              {generating ? (
                <Tooltip title={intl.formatMessage(messages.stop)}>
                  <Button
                    icon={<FontAwesomeIcon icon={faSquare} />}
                    iconOnly
                    onClick={onStop}
                    size="small"
                  />
                </Tooltip>
              ) : (
                <>
                  {hasValue && focused && (
                    <div className={styles.helper}>
                      <div className={styles.enterIcon}>
                        <FontAwesomeIcon icon={faArrowTurnDownLeft} size="2xs" />
                      </div>
                      <span>{intl.formatMessage(messages.write)}</span>
                    </div>
                  )}
                  <Tooltip title={intl.formatMessage(messages.cancel)}>
                    <Button
                      icon={<FontAwesomeIcon icon={faXmark} />}
                      iconOnly
                      onClick={onClose}
                      size="small"
                    />
                  </Tooltip>
                  {hasGeneratedContent && !hasValue ? (
                    <Tooltip title={intl.formatMessage(messages.insert)}>
                      <Button
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        iconOnly
                        onClick={onConfirm}
                        size="small"
                        type="primary"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      title={intl.formatMessage(messages.write)}
                      trigger={!value.trim() ? [] : ["hover"]}
                    >
                      <Button
                        className={styles.submitButton}
                        disabled={!value.trim()}
                        icon={<FontAwesomeIcon icon={faArrowUp} />}
                        iconOnly
                        onClick={onSubmit}
                        size="small"
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          }
          value={value}
        />
      </div>
    </Dropdown>
  );
};

export default PromptInput;
